// The shared transport bar (iReal Pro model): Play is the anchor —
// centered and enlarged — with the mixer on the left and playback toggles on
// the right. Stop sits beside Play while playback is active. Above
// it, a strip of summary chips (tempo, repeats, style) each open a focused
// drawer *panel* rather than cramming every control inline; only one panel is
// open at a time. Band, Mixer and About open the same drawer from row icons.
// This keeps the bar readable on a phone while still exposing every setting one
// tap away. The bar is fixed at the viewport bottom on every page: a panel comes
// to the reader, so nothing about the music underneath has to move.

import {
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Repeat, SkipBack, SkipForward, X } from 'lucide-react';
import { getChordPlayer, type TransportState } from '@/audio/player';
import { useOwnHeight } from './useOwnHeight';

type TransportControlVariant = 'round' | 'primary' | 'preset' | 'row' | 'ghost';

interface TransportControlButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: TransportControlVariant;
  active?: boolean;
}

export function TransportControlButton({
  variant = 'round',
  active = false,
  className = '',
  type = 'button',
  ...props
}: TransportControlButtonProps) {
  const classes = [
    'transport-control-button',
    `transport-control-${variant}`,
    active ? 'is-active' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return <button type={type} className={classes} {...props} />;
}

export interface TransportPanel {
  id: string;
  title: string;
  /**
   * Drawer body shown when this panel is the active one. A function form
   * receives the drawer's close fn (as `Popover` does), so a panel that
   * navigates can dismiss itself.
   */
  content: ReactNode | ((close: () => void) => ReactNode);
  /** Tappable summary chip in the strip (tempo / repeats / style). */
  chip?: { value: ReactNode; caption: string; testId?: string; ariaLabel?: string };
  /** Icon trigger in the transport row's left slot (Band, Mixer or About). */
  icon?: { glyph: ReactNode; label: string; testId?: string };
}

export interface TransportAction {
  id: string;
  label: string;
  glyph: ReactNode;
  pressed: boolean;
  onClick: () => void;
  testId?: string;
}

/**
 * Chorus skips, flanking Play while the band plays. Forward goes to the next
 * chorus (holding it goes to the last), back restarts this one. The player
 * decides where each lands; the bar only asks.
 */
export interface ChorusSkip {
  onBack: () => void;
  onForward: () => void;
  /** Hold forward: straight to the last chorus. Absent when there is none. */
  onForwardHold?: () => void;
  canForward: boolean;
}

/** How long a press on forward must last to read as a hold. */
const SKIP_HOLD_MS = 500;

function SkipForwardButton({ skip }: { skip: ChorusSkip }) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const held = useRef(false);
  const clear = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };
  return (
    <TransportControlButton
      className="transport-skip transport-skip-forward"
      aria-label="Next chorus"
      title={skip.onForwardHold ? 'Next chorus · hold for the last' : 'Next chorus'}
      data-testid="transport-skip-forward"
      disabled={!skip.canForward}
      onPointerDown={() => {
        held.current = false;
        clear();
        if (!skip.onForwardHold) return;
        timer.current = setTimeout(() => {
          held.current = true;
          timer.current = null;
          skip.onForwardHold?.();
        }, SKIP_HOLD_MS);
      }}
      onPointerUp={clear}
      onPointerLeave={clear}
      onPointerCancel={clear}
      onContextMenu={(event) => event.preventDefault()}
      onClick={() => {
        clear();
        if (held.current) {
          held.current = false;
          return;
        }
        skip.onForward();
      }}
    >
      <SkipForward size={18} strokeWidth={2} aria-hidden="true" />
    </TransportControlButton>
  );
}

interface TransportBarProps {
  transport: TransportState;
  /** Sound off hides the play/stop cluster but keeps the chips + drawer. */
  audioEnabled: boolean;
  onPlay: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onStop?: () => void;
  /** A bar-range is selected: Play starts that span and loops it. */
  looping?: boolean;
  loopLabel?: string | null;
  /** Dismisses the selected bar range; shown on the loop strip. */
  onClearLoop?: () => void;
  /** The bar Play starts from (a tapped bar); the loop strip wins while a loop is armed. */
  startBar?: number | null;
  /** Sends the start back to the top. */
  onClearStart?: () => void;
  /** What is in force at the playing bar (#415): "Solo 2", "Tag 2 of 3", "rit.". */
  status?: string[];
  panels: TransportPanel[];
  actions?: TransportAction[];
  /** Chorus skips beside Play while the band plays. */
  skip?: ChorusSkip;
}

/** Three horizontal faders — the mixer/options glyph (no icon-font dependency). */
function MixerGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
      <circle cx="9" cy="7" r="2.2" fill="var(--surface)" />
      <circle cx="15" cy="12" r="2.2" fill="var(--surface)" />
      <circle cx="8" cy="17" r="2.2" fill="var(--surface)" />
    </svg>
  );
}

export function TransportBar({
  transport,
  audioEnabled,
  onPlay,
  onPause,
  onResume,
  onStop,
  looping = false,
  loopLabel,
  onClearLoop,
  startBar,
  onClearStart,
  status,
  panels,
  actions = [],
  skip,
}: TransportBarProps) {
  // The bar's live height (drawer included) on :root — a popover from the
  // pinned workspace strips fits the band above it (tuneWorkspace.css).
  const measureTransport = useOwnHeight('--transport-height', '--transport-height');
  const measureChrome = useOwnHeight('--transport-chrome-height', '--transport-chrome-height');
  const [active, setActive] = useState<string | null>(null);
  const close = () => setActive(null);
  const toggle = (id: string) => setActive((cur) => (cur === id ? null : id));
  const activePanel = panels.find((p) => p.id === active) ?? null;
  const chips = panels.filter((p) => p.chip);
  const icons = panels.filter((p) => p.icon);
  const idle = transport === 'idle';

  return (
    <div className="transport-bar" data-testid="transport-bar" ref={measureTransport}>
      {activePanel && (
        <div
          className="transport-drawer"
          data-testid="transport-drawer"
          data-panel={activePanel.id}
        >
          <div className="transport-drawer-head">
            <span className="transport-drawer-title">
              {activePanel.icon?.glyph}
              {activePanel.title}
            </span>
            <TransportControlButton
              variant="ghost"
              className="transport-drawer-close close-circle"
              aria-label={`Close ${activePanel.title.toLowerCase()}`}
              onClick={close}
            >
              <X size={15} aria-hidden="true" />
            </TransportControlButton>
          </div>
          <div
            key={activePanel.id}
            className="transport-drawer-body"
            data-testid="transport-drawer-body"
          >
            {typeof activePanel.content === 'function'
              ? activePanel.content(close)
              : activePanel.content}
          </div>
        </div>
      )}

      <div className="transport-chrome" ref={measureChrome}>
        {/* A range made by a stray drag changes what Play does, so the transport
            says so and offers the way back to the whole tune. */}
        {looping && loopLabel && (
          <div className="transport-loop" data-testid="transport-loop">
            <Repeat size={14} strokeWidth={2.25} aria-hidden="true" />
            <span className="transport-loop-label">Loop · {loopLabel}</span>
            {onClearLoop && (
              <button
                type="button"
                className="transport-loop-clear"
                aria-label="Clear loop"
                data-testid="transport-loop-clear"
                onClick={onClearLoop}
              >
                <X size={14} aria-hidden="true" />
                <span>Clear</span>
              </button>
            )}
          </div>
        )}
        {/* A tapped bar moves where Play starts, which a reader cannot see
            once the band has moved on, so the transport names it. */}
        {!looping && startBar != null && (
          <div className="transport-loop transport-start" data-testid="transport-start">
            <span className="transport-start-mark" aria-hidden="true" />
            <span className="transport-loop-label">From bar {startBar}</span>
            {onClearStart && (
              <button
                type="button"
                className="transport-loop-clear"
                aria-label="Play from the top"
                data-testid="transport-start-clear"
                onClick={onClearStart}
              >
                <X size={14} aria-hidden="true" />
                <span>Top</span>
              </button>
            )}
          </div>
        )}
        {status && status.length > 0 && (
          <div className="transport-status" data-testid="transport-status" aria-live="polite">
            {status.map((item) => (
              <span key={item} className="transport-status-item">
                {item}
              </span>
            ))}
          </div>
        )}
        {chips.length > 0 && (
          <div className="transport-strip" style={{ '--chips': chips.length } as CSSProperties}>
            {chips.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`transport-chip${active === p.id ? ' on' : ''}`}
                aria-expanded={active === p.id}
                aria-label={p.chip!.ariaLabel}
                data-testid={p.chip!.testId}
                onClick={() => toggle(p.id)}
              >
                <span className="transport-chip-value">{p.chip!.value}</span>
                <span className="transport-chip-caption">{p.chip!.caption}</span>
              </button>
            ))}
          </div>
        )}

        <div className="transport-row">
          <div className="transport-slot transport-slot-left">
            {icons.map((p) => (
              <TransportControlButton
                key={p.id}
                active={active === p.id}
                aria-expanded={active === p.id}
                aria-label={p.icon!.label}
                title={p.icon!.label}
                data-testid={p.icon!.testId}
                onClick={() => toggle(p.id)}
              >
                {p.icon!.glyph}
              </TransportControlButton>
            ))}
          </div>

          <div
            className="transport-slot transport-slot-center"
            data-skips={audioEnabled && !idle && skip ? true : undefined}
          >
            {audioEnabled && !idle && skip && (
              <TransportControlButton
                className="transport-skip transport-skip-back"
                aria-label="Restart chorus"
                title="Restart chorus · twice for the one before"
                data-testid="transport-skip-back"
                onClick={skip.onBack}
              >
                <SkipBack size={18} strokeWidth={2} aria-hidden="true" />
              </TransportControlButton>
            )}
            {audioEnabled && !idle && skip && <SkipForwardButton skip={skip} />}
            {audioEnabled && !idle && (
              <TransportControlButton
                className="transport-stop-adjacent"
                aria-label="Stop"
                data-testid="transport-stop"
                onClick={onStop ?? (() => getChordPlayer().stop())}
              >
                <span className="transport-stop-icon" aria-hidden="true" />
              </TransportControlButton>
            )}
            {audioEnabled &&
              (idle ? (
                <TransportControlButton
                  variant="primary"
                  aria-label={looping ? `Play loop${loopLabel ? `, ${loopLabel}` : ''}` : 'Play'}
                  title={looping ? `Play loop${loopLabel ? ` · ${loopLabel}` : ''}` : 'Play'}
                  data-testid="transport-play"
                  data-looping={looping || undefined}
                  onClick={onPlay}
                >
                  {looping ? (
                    <Repeat size={26} strokeWidth={2.25} aria-hidden="true" />
                  ) : (
                    <span
                      className="transport-primary-icon transport-primary-icon-play"
                      aria-hidden="true"
                    />
                  )}
                </TransportControlButton>
              ) : transport === 'playing' ? (
                <TransportControlButton
                  variant="primary"
                  aria-label="Pause"
                  data-testid="transport-pause"
                  onClick={onPause ?? (() => getChordPlayer().pause())}
                >
                  <span
                    className="transport-primary-icon transport-primary-icon-pause"
                    aria-hidden="true"
                  />
                </TransportControlButton>
              ) : (
                <TransportControlButton
                  variant="primary"
                  aria-label={
                    looping ? `Resume loop${loopLabel ? `, ${loopLabel}` : ''}` : 'Resume'
                  }
                  title={looping ? `Resume loop${loopLabel ? ` · ${loopLabel}` : ''}` : 'Resume'}
                  data-testid="transport-resume"
                  data-looping={looping || undefined}
                  onClick={onResume ?? (() => getChordPlayer().resumePlayback())}
                >
                  {looping ? (
                    <Repeat size={26} strokeWidth={2.25} aria-hidden="true" />
                  ) : (
                    <span
                      className="transport-primary-icon transport-primary-icon-play"
                      aria-hidden="true"
                    />
                  )}
                </TransportControlButton>
              ))}
          </div>

          <div className="transport-slot transport-slot-right">
            {actions.map((action) => (
              <TransportControlButton
                key={action.id}
                active={action.pressed}
                aria-label={action.label}
                aria-pressed={action.pressed}
                title={action.label}
                data-testid={action.testId}
                onClick={action.onClick}
              >
                {action.glyph}
              </TransportControlButton>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export { MixerGlyph };
